/* eslint-disable no-nested-ternary */
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { BiLogOut } from "react-icons/bi";
import TopHeader from "../../components/common/TopHeader/TopHeader";
import RetrofitStepper from "../../components/Retrofit/RetrofitStepper/RetrofitStepper";
import {
  resetRetrofit,
  selectReadOnlyMode,
  selectShowAxisPopup,
  closeSnackbar,
  selectSnackbar,
  selectRetrofitConfiguration,
  selectRetrofitCurrentStep
} from "../../store/slices/retrofit";

import t from "../../utils/common/translation/translator";
import AxisRecapHeader from "../../components/Retrofit/SpecifySelect/AxisRecapHeader";
import Snackbar from "../../components/common/Snackbar/Snackbar";
import useCheckMobileScreen from "../../utils/common/useCheckMobileScreen";
import CartesianStepper from "../../components/Cartesian/CartesianSteper/CartesianStepper";
import { getAllSteps, isStartStep } from "../../store/utils/utils";
import { resetCartesian, selectCartesianConfiguration, selectCartesianCurrentStep } from "../../store/slices/cartesian";
import { resetCommon, selectConfigurationType, setShowCancelDialogPopup } from "../../store/slices/common";
import Mc12Stepper from "../../components/Mc12/Mc12Stepper/Mc12Stepper";
import { resetMc12, selectMc12Configuration, selectMc12CurrentStep } from "../../store/slices/mc12";
import IntegratedDrivesStepper from "../../components/IntegratedDrives/IntegratedDrivesStepper/IntegratedDrivesStepper";
import {
  resetIntegratedDrives,
  selectIntegratedDrivesConfiguration,
  selectIntegratedDrivesCurrentStep
} from "../../store/slices/integrated_drives";

function getStepper(configurationType) {
  switch (configurationType) {
    case "MC12":
      return <Mc12Stepper />;
    case "CARTESIAN":
      return <CartesianStepper />;
    case "RETROFIT":
      return <RetrofitStepper />;
    case "INTEGRATED_DRIVES":
      return <IntegratedDrivesStepper />;
    default:
      return null;
  }
}

/**
 * Whole header of the app.
 */
const Header = () => {
  const dispatch = useDispatch();

  const showAxisPopup = useSelector(selectShowAxisPopup);
  const readOnlyMode = useSelector(selectReadOnlyMode);
  const steps = getAllSteps();
  const retrofitConfiguration = useSelector(selectRetrofitConfiguration);
  const cartesianConfiguration = useSelector(selectCartesianConfiguration);
  const mc12Configuration = useSelector(selectMc12Configuration);
  const integratedDrivesConfiguration = useSelector(selectIntegratedDrivesConfiguration);
  // it's important to start with cartesianConfiguration, because we can have a retrofit configuration which is a part of the cartesian configuration
  const configuration =
    cartesianConfiguration || retrofitConfiguration || mc12Configuration || integratedDrivesConfiguration;
  const configurationType = useSelector(selectConfigurationType);
  const retrofitCurrentStep = useSelector(selectRetrofitCurrentStep);
  const cartesianCurrentStep = useSelector(selectCartesianCurrentStep);
  const mc12CurrentStep = useSelector(selectMc12CurrentStep);
  const integratedDrivesCurrentStep = useSelector(selectIntegratedDrivesCurrentStep);

  let currentStep = retrofitCurrentStep;
  if (configurationType === "CARTESIAN") {
    currentStep = cartesianCurrentStep;
  } else if (configurationType === "MC12") {
    currentStep = mc12CurrentStep;
  } else if (configurationType === "INTEGRATED_DRIVES") {
    currentStep = integratedDrivesCurrentStep;
  }
  const snackbar = useSelector(selectSnackbar);
  const isMobile = useCheckMobileScreen();

  const handleIconExitConfigurationClick = () => {
    if (!readOnlyMode && configuration.configData.configStatus === "IN_PROGRESS") {
      dispatch(setShowCancelDialogPopup(true));
    } else {
      dispatch(resetRetrofit());
      dispatch(resetCartesian());
      dispatch(resetMc12());
      dispatch(resetIntegratedDrives());
      dispatch(resetCommon());
    }
  };

  const isStartStepCheck = isStartStep(currentStep);

  return (
    <div className="sticky top-0 bg-gray-50 md:bg-white w-full z-30">
      <TopHeader />
      <div className="bg-white md:mx-5">
        {(!showAxisPopup || true) && (
          <div className="flex flex-col space-x-4 px-5 md:px-0">
            <div className="flex items-center text-xl mt-2 print:hidden">
              {!isStartStepCheck && (
                <BiLogOut
                  id="general-header-button-exit"
                  onClick={() => handleIconExitConfigurationClick()}
                  className="text-3xl text-secondary mr-2 cursor-pointer border-r border-gray-300 pr-2"
                />
              )}
              <div className="flex w-full text-gray-700">
                <div className="flex flex-col">
                  <span className={`app-title font-bold ${isStartStepCheck ? "text-2xl text-gray-500" : "text-sm"}`}>
                    {configurationType === "CARTESIAN"
                      ? t("cartesian.ui.start-config-card-title")
                      : configurationType === "MC12"
                      ? t("mc12.ui.start-config-card-title")
                      : configurationType === "INTEGRATED_DRIVES"
                      ? t("integrated_drives.ui.start-config-card-title")
                      : configurationType === "RETROFIT"
                      ? t("msizer.ui.start-config-card-title")
                      : "Motion Control and Robotics Configurator"}
                  </span>
                  {!isStartStepCheck && <span className=" text-xxs">Motion Control and Robotics Configurator</span>}
                </div>
                {process.env.REACT_APP_ENVIRONMENT_TYPE !== undefined &&
                  process.env.REACT_APP_ENVIRONMENT_TYPE !== "PROD" && (
                    <div className="flex-1 flex items-center justify-center">
                      <span className="font-semibold text-primary">
                        {process.env.REACT_APP_ENVIRONMENT_TYPE} - Release {process.env.REACT_APP_VERSION}
                      </span>
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}
        {getStepper(configurationType)}
        <div
          className={`${isMobile ? "fixed top-3 w-full z-50" : "block"}
          ${isMobile ? "fixed -top-8 w-full z-50" : "block"}
           ${isStartStep(currentStep) ? "relative top-14" : "block"}`}
        >
          <Snackbar
            id={snackbar.id}
            opened={snackbar.show}
            onClose={() => dispatch(closeSnackbar(snackbar.id))}
            hideCTA={snackbar.hideCTA}
            customMessage={snackbar.customMessage}
          />
        </div>
      </div>
      {/* show AxisRecapHeader if applicationDataFilters are applied for at least one axis */}
      {currentStep === steps.retrofit.SPECIFY_SELECT &&
        configuration?.configData.axes.some((axis) => axis.axisState.applicationDataFiltersApplied === true) && (
          <AxisRecapHeader />
        )}
    </div>
  );
};

export default Header;
